import React, { useState, useEffect } from "react";
import { Form, Button, Container, Row, Col, Alert, Spinner, Card, Badge } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const calculateAge = (dobString) => {
    if (!dobString) return null;
    const today = new Date();
    const birthDate = new Date(dobString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

const getAgeGroup = (age) => {
    if (age === null || age === undefined || isNaN(age)) return "";
    if (age <= 12) return "Kids (0-12)";
    if (age <= 19) return "Teens (13-19)";
    if (age <= 59) return "Adults (20-59)";
    return "Seniors (60+)";
};

const VisitorRegistration = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [serverMsg, setServerMsg] = useState("");
    const [errors, setErrors] = useState({});
    const [isFirstTime, setIsFirstTime] = useState(null); // null = select screen, true = new visitor, false = returning
    const [identifier, setIdentifier] = useState(""); // returning visitor email or mobile
    const [hoveredBtnNew, setHoveredBtnNew] = useState(false);
    const [hoveredBtnRepeat, setHoveredBtnRepeat] = useState(false);
    const [consentChecked, setConsentChecked] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false); // true = update mode
    const [updateIdentifier, setUpdateIdentifier] = useState(""); // email or mobile to find profile
    const [updateStep, setUpdateStep] = useState(1); // 1 = enter identifier, 2 = show form
    const [foundVisitorId, setFoundVisitorId] = useState(""); // visitorId of found visitor
    const [isGroupVisit, setIsGroupVisit] = useState(false);
    const [showThankYou, setShowThankYou] = useState(false);
    const [isSecondaryReturning, setIsSecondaryReturning] = useState(false);
    const [registeredVisitorId, setRegisteredVisitorId] = useState("");
    const [registeredName, setRegisteredName] = useState("");
    const [profileJustCompleted, setProfileJustCompleted] = useState(false);
    const [resolvedName, setResolvedName] = useState("");
    const [isLookingUp, setIsLookingUp] = useState(false);
    const [otherPurpose, setOtherPurpose] = useState("");

    const [formData, setFormData] = useState({
        // Required Fields
        fullName: "",
        mobileNumber: "",
        email: "",
        address: "",
        // Personal
        gender: "",
        dob: "",
        ageGroup: "",
        // Location
        area: "",
        city: "",
        state: "",
        pincode: "",
        // Professional
        occupation: "",
        organizationName: "",
        // Visit Info
        branch: "",
        purposeOfVisit: "",
        // Interests
        interestedCauses: [],
        // Preferences
        preferredContactMethod: "WhatsApp",
        receiveUpdates: "Yes",
        notes: ""
    });

    // Prefill form if primary visitor data already in sessionStorage (from Back button or step persistence)
    useEffect(() => {
        const stored = sessionStorage.getItem("tmgf_primary_visitor");
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                setIsGroupVisit(true);
                if (parsed.isFirstTime === false) {
                    setIsFirstTime(false);
                    setIdentifier(parsed.identifier || "");
                    setFormData(prev => ({
                        ...prev,
                        purposeOfVisit: parsed.purposeOfVisit || "General Inquiry",
                        notes: parsed.notes || ""
                    }));
                } else if (parsed.isFirstTime === true) {
                    setIsFirstTime(true);
                    setFormData({
                        fullName: parsed.fullName || "",
                        mobileNumber: parsed.mobileNumber || "",
                        email: parsed.email || "",
                        address: parsed.address || "",
                        gender: parsed.gender || "",
                        ageGroup: parsed.ageGroup || "",
                        area: parsed.area || "",
                        city: parsed.city || "",
                        state: parsed.state || "",
                        pincode: parsed.pincode || "",
                        occupation: parsed.occupation || "",
                        organizationName: parsed.organizationName || "",
                        purposeOfVisit: parsed.purposeOfVisit || "General Inquiry",
                        interestedCauses: parsed.interestedCauses || [],
                        preferredContactMethod: parsed.preferredContactMethod || "WhatsApp",
                        branch: parsed.branch || "",
                        receiveUpdates: parsed.receiveUpdates || "Yes",
                        notes: parsed.notes || ""
                    });
                }
            } catch (e) {
                console.error("Error loading prefilled state:", e);
            }
        }
    }, []);

    // Real-time lookup for returning visitor
    useEffect(() => {
        const trimmed = (identifier || "").trim();
        const emailRegex = /^[a-zA-Z0-9._%+-]+@(gmail\.com|yahoo\.com|outlook\.com|hotmail\.com|icloud\.com|rediffmail\.com|ymail\.com|protonmail\.com|live\.com|msn\.com|me\.com|coep\.ac\.in|tmgf\.in|[a-zA-Z0-9-]+\.(edu|ac\.in|org|gov|nic\.in))$/i;
        const mobileRegex = /^[6-9]\d{9}$/;

        if (emailRegex.test(trimmed) || mobileRegex.test(trimmed)) {
            const performLookup = async () => {
                setIsLookingUp(true);
                setResolvedName("");
                try {
                    const response = await fetch(`${BASE_URL}/api/visitors/lookup`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ identifier: trimmed })
                    });
                    if (response.ok) {
                        const data = await response.json();
                        if (data && data.fullName) {
                            setResolvedName(data.fullName);
                        } else {
                            setResolvedName("Not Registered");
                        }
                    } else {
                        setResolvedName("Not Registered");
                    }
                } catch (err) {
                    console.error("Lookup error:", err);
                    setResolvedName("Not Registered");
                } finally {
                    setIsLookingUp(false);
                }
            };
            performLookup();
        } else {
            setResolvedName("");
        }
    }, [identifier]);

    // Autofill city and state from pincode using api.postalpincode.in
    useEffect(() => {
        const pincodeVal = (formData.pincode || "").trim();
        if (/^\d{6}$/.test(pincodeVal)) {
            const fetchLocation = async () => {
                try {
                    const response = await fetch(`https://api.postalpincode.in/pincode/${pincodeVal}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (data && data[0] && data[0].Status === "Success" && data[0].PostOffice && data[0].PostOffice[0]) {
                            const postOffice = data[0].PostOffice[0];
                            const city = postOffice.District || "";
                            const state = postOffice.State || "";
                            setFormData(prev => ({
                                ...prev,
                                city: city,
                                state: state
                            }));
                            setErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors.city;
                                delete newErrors.state;
                                delete newErrors.pincode;
                                return newErrors;
                            });
                        } else {
                            setErrors(prev => ({ ...prev, pincode: "Enter correct pincode" }));
                            setFormData(prev => ({ ...prev, city: "", state: "" }));
                        }
                    } else {
                        setErrors(prev => ({ ...prev, pincode: "Enter correct pincode" }));
                        setFormData(prev => ({ ...prev, city: "", state: "" }));
                    }
                } catch (error) {
                    console.error("Error fetching pincode data:", error);
                    setErrors(prev => ({ ...prev, pincode: "Enter correct pincode" }));
                    setFormData(prev => ({ ...prev, city: "", state: "" }));
                }
            };
            fetchLocation();
        } else {
            setErrors(prev => {
                if (prev.pincode === "Enter correct pincode" || prev.pincode === "Invalid Pincode (6 digits required)") {
                    const newErrors = { ...prev };
                    delete newErrors.pincode;
                    return newErrors;
                }
                return prev;
            });
        }
    }, [formData.pincode]);

    // Lookup visitor profile for update
    const handleLookupForUpdate = async () => {
        if (!updateIdentifier.trim()) return;
        setIsSubmitting(true);
        try {
            const response = await fetch(`${BASE_URL}/api/visitors/lookup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ identifier: updateIdentifier })
            });
            const data = await response.json();
            if (!response.ok) {
                toast.error(data.message || 'No profile found.');
                return;
            }
            // Pre-fill form with existing data
            setFormData({
                fullName: data.fullName || "",
                mobileNumber: data.mobileNumber || "",
                email: data.email || "",
                address: data.address || "",
                gender: data.gender || "",
                dob: data.dob || "",
                ageGroup: data.ageGroup || "",
                area: data.area || "",
                city: data.city || "",
                state: data.state || "",
                pincode: data.pincode || "",
                occupation: data.occupation || "",
                organizationName: data.organizationName || "",
                purposeOfVisit: "",
                branch: data.branch || "",
                interestedCauses: data.interestedCauses || [],
                preferredContactMethod: data.preferredContactMethod || "WhatsApp",
                receiveUpdates: data.receiveUpdates || "Yes",
                notes: ""
            });
            setFoundVisitorId(data.visitorId);
            setUpdateStep(2);
            toast.success(`Profile found! Welcome, ${data.fullName}. Update your details below.`);
        } catch (err) {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Submit updated details
    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            toast.error("Please correct the errors before submitting.");
            return;
        }
        setIsSubmitting(true);
        try {
            const response = await fetch(`${BASE_URL}/api/visitors/update/${foundVisitorId}`, {
                //const response = await fetch(`https://vms-pjum.onrender.com/api/visitors/update/${foundVisitorId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            const data = await response.json();
            if (!response.ok) {
                toast.error(data.message || 'Update failed.');
                return;
            }
            // toast.success('Your details have been updated successfully!');
            // setTimeout(() => {
            //     setIsUpdating(false);
            //     setUpdateStep(1);
            //     setUpdateIdentifier("");
            //     setFoundVisitorId("");
            //     setFormData({
            //         fullName: "", mobileNumber: "", email: "", address: "",
            //         gender: "", ageGroup: "", area: "", city: "", state: "", pincode: "",
            //         occupation: "", organizationName: "", purposeOfVisit: "",branch: "",
            //         interestedCauses: [], preferredContactMethod: "WhatsApp", receiveUpdates: "Yes", notes: ""
            //     });
            // }, 3000);
            toast.success('Your details have been updated successfully!');
            setRegisteredName(formData.fullName);
            setRegisteredVisitorId(foundVisitorId);
            setIsFirstTime(false);
            setProfileJustCompleted(true);
            setShowThankYou(true);
        } catch (err) {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const causesOptions = [
        "Education", "Healthcare", "Environment", "Women Empowerment",
        "Child Welfare", "Skill Development", "Community Development",
        "Rural Development", "Animal Welfare", "Disaster Relief"
    ];

    const validateForm = () => {
        const newErrors = {};
        const emailRegex = /^[a-zA-Z0-9._%+-]+@(gmail\.com|yahoo\.com|outlook\.com|hotmail\.com|icloud\.com|rediffmail\.com|ymail\.com|protonmail\.com|live\.com|msn\.com|me\.com|coep\.ac\.in|tmgf\.in|[a-zA-Z0-9-]+\.(edu|ac\.in|org|gov|nic\.in))$/i;
        const mobileRegex = /^[6-9]\d{9}$/;
        const pincodeRegex = /^\d{6}$/;
        const nameRegex = /^[A-Za-z]+(?:[' -][A-Za-z]+)*$/;

        const trimmedFullName = (formData.fullName || "").trim();
        const trimmedMobile = (formData.mobileNumber || "").trim();
        const trimmedEmail = (formData.email || "").trim();
        const trimmedBranch = (formData.branch || "").trim();
        const trimmedAddress = (formData.address || "").trim();
        const trimmedPincode = (formData.pincode || "").trim();
        const trimmedIdentifier = (identifier || "").trim();

        // Update mode validation
        if (isUpdating) {
            if (!trimmedFullName) {
                newErrors.fullName = "Full Name is required";
            } else if (!nameRegex.test(trimmedFullName)) {
                newErrors.fullName = "Please enter a valid name (letters, single spaces, apostrophes/hyphens only)";
            }
            if (!trimmedBranch) newErrors.branch = "Branch is required";
            if (!trimmedMobile) {
                newErrors.mobileNumber = "Mobile number is required";
            } else if (!mobileRegex.test(trimmedMobile)) {
                newErrors.mobileNumber = "Mobile number must be a valid 10-digit number starting with 6-9";
            }
            if (trimmedEmail && !emailRegex.test(trimmedEmail)) {
                newErrors.email = "Please enter a valid email address";
            }
            //if (!trimmedAddress) newErrors.address = "Address is required";
            if (trimmedPincode) {
                if (!pincodeRegex.test(trimmedPincode)) {
                    newErrors.pincode = "Invalid Pincode (6 digits required)";
                } else if (errors.pincode === "Enter correct pincode") {
                    newErrors.pincode = "Enter correct pincode";
                }
            }
            if (formData.purposeOfVisit === "Other" && !otherPurpose.trim()) {
                newErrors.purposeOfVisit = "Please describe your purpose of visit";
            }
        } else if (isFirstTime === false) {
            // Returning visitor: Identifier must be a valid email or 10-digit mobile
            if (!trimmedIdentifier) {
                newErrors.identifier = "Valid email or 10-digit mobile number is required";
            } else {
                const isEmail = emailRegex.test(trimmedIdentifier);
                const isMobile = mobileRegex.test(trimmedIdentifier);
                if (!isEmail && !isMobile) {
                    newErrors.identifier = "Valid email or 10-digit mobile number is required";
                }
            }
            if (!trimmedBranch) newErrors.branch = "Branch is required";
        } else if (isFirstTime === true) {
            // Only name and phone are mandatory
            if (!trimmedFullName) {
                newErrors.fullName = "Full Name is required";
            } else if (!nameRegex.test(trimmedFullName)) {
                newErrors.fullName = "Please enter a valid name (letters, single spaces, apostrophes/hyphens only)";
            }
            if (!trimmedBranch) newErrors.branch = "Branch is required";
            if (!trimmedMobile) {
                newErrors.mobileNumber = "Mobile number is required";
            } else if (!mobileRegex.test(trimmedMobile)) {
                newErrors.mobileNumber = "Mobile number must be a valid 10-digit number starting with 6-9";
            }
            // Email optional but if filled must be valid
            if (trimmedEmail && !emailRegex.test(trimmedEmail)) {
                newErrors.email = "Enter a valid email or leave it blank";
            }
            // Pincode optional but if filled must be valid
            if (trimmedPincode) {
                if (!pincodeRegex.test(trimmedPincode)) {
                    newErrors.pincode = "Invalid Pincode (6 digits required)";
                } else if (errors.pincode === "Enter correct pincode") {
                    newErrors.pincode = "Enter correct pincode";
                }
            }
            if (!formData.purposeOfVisit) {
                newErrors.purposeOfVisit = "Please select a purpose of visit";
            } else if (formData.purposeOfVisit === "Other" && !otherPurpose.trim()) {
                newErrors.purposeOfVisit = "Please describe your purpose of visit";
            }
        }

        if (formData.dob) {
            const birthDate = new Date(formData.dob);
            const today = new Date();
            if (birthDate > today) {
                newErrors.dob = "Date of Birth cannot be in the future";
            } else if (today.getFullYear() - birthDate.getFullYear() > 120) {
                newErrors.dob = "Invalid Date of Birth";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };



    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "dob") {
            const age = calculateAge(value);
            const ageGroup = getAgeGroup(age);
            setFormData(prev => ({
                ...prev,
                dob: value,
                ageGroup: ageGroup
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleInterestChange = (cause) => {
        setFormData(prev => {
            const current = [...prev.interestedCauses];
            if (current.includes(cause)) {
                return { ...prev, interestedCauses: current.filter(c => c !== cause) };
            } else {
                return { ...prev, interestedCauses: [...current, cause] };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setServerMsg("");

        if (!validateForm()) {
            toast.error("Please correct the errors before submitting.");
            return;
        }

        const payload = {
            isFirstTime,
            purposeOfVisit: formData.purposeOfVisit === "Other" ? (otherPurpose.trim() || "") : formData.purposeOfVisit,
            notes: formData.notes,
            submittedAt: new Date().toISOString(),
            source: "Visitor Portal"
        };

        if (isFirstTime === false) {
            payload.identifier = identifier;
            payload.mobileNumber = identifier;
        } else {
            payload.fullName = formData.fullName;
            payload.mobileNumber = formData.mobileNumber;
            payload.email = formData.email;
            payload.address = formData.address;
            payload.gender = formData.gender;
            payload.ageGroup = formData.ageGroup;
            payload.area = formData.area;
            payload.city = formData.city;
            payload.state = formData.state;
            payload.pincode = formData.pincode;
            payload.occupation = formData.occupation;
            payload.organizationName = formData.organizationName;
            payload.interestedCauses = formData.interestedCauses;
            payload.preferredContactMethod = formData.preferredContactMethod;
            payload.receiveUpdates = formData.receiveUpdates;
            payload.branch = formData.branch;
            payload.dob = formData.dob;
        }

        if (isGroupVisit) {
            sessionStorage.setItem("tmgf_primary_visitor", JSON.stringify(payload));
            navigate("/visitor/group-details");
            return;
        }

        setIsSubmitting(true);

        console.log("🚀 Visitor Registration Payload:", JSON.stringify(payload, null, 2));

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/visitors/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (!response.ok) {
                // 1. Returning check-in NOT found (HTTP 404) -> Auto-redirect to registration
                if (response.status === 404 && data.redirect === 'first-time') {
                    setServerMsg(data.message || "No profile found. Redirecting you to register...");
                    toast.warn("Profile not found! Automatically redirecting you to first-time visitor registration...");

                    setTimeout(() => {
                        setIsFirstTime(true);
                        const isEmail = /^(?!.*?\.\.)[a-zA-Z0-9]([a-zA-Z0-9._%+-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test((identifier || "").trim());
                        setFormData(prev => ({
                            ...prev,
                            mobileNumber: isEmail ? "" : identifier,
                            email: isEmail ? identifier : ""
                        }));
                        setServerMsg("We pre-filled your phone/email! Please complete your registration below.");
                        setErrors({});
                    }, 3000);

                    setIsSubmitting(false);
                    return;
                }

                // 2. First-time registration ALREADY registered (HTTP 409) -> Auto-redirect to returning check-in
                if (response.status === 409 && data.redirect === 'returning') {
                    setServerMsg(data.message || "A profile is already registered.");
                    toast.warn("Profile already registered! Automatically redirecting you to returning check-in...");

                    setTimeout(() => {
                        setIsFirstTime(false);
                        setIdentifier(data.typedIdentifier || "");
                        setServerMsg("Welcome back! Your profile was found. Please log your visit details below.");
                        setErrors({});
                    }, 3000);

                    setIsSubmitting(false);
                    return;
                }

                // 3. Duplicate visit check (HTTP 429) -> Show error message
                if (response.status === 429) {
                    setServerMsg(data.message || "This visitor was already checked in recently.");
                    toast.error(data.message || "Duplicate check-in detected!");
                    setIsSubmitting(false);
                    return;
                }

                throw new Error(data.message || 'Something went wrong');
            }

            // Immediately show Thank You screen with Visitor ID
            const vid = data.visitor?.visitorId || "";
            const vname = data.visitor?.fullName || formData.fullName || identifier || "";
            setRegisteredVisitorId(vid);
            setRegisteredName(vname);
            // Check if secondary visitor is returning
            setIsSecondaryReturning(isFirstTime === false && data.showCompleteProfile === true);
            setShowThankYou(true);
            sessionStorage.removeItem("tmgf_primary_visitor");
        } catch (err) {
            setServerMsg("Something went wrong. Please check your connection and try again.");
            toast.error("Submission failed. Please check your connection.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputStyle = {
        padding: "12px",
        borderRadius: "8px",
        border: "1px solid #ddd",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        fontSize: "0.95rem"
    };

    const sectionTitleStyle = {
        color: "#ac2e62",
        fontSize: "1.1rem",
        fontWeight: "700",
        borderBottom: "2px solid #f0f0f0",
        paddingBottom: "8px",
        marginBottom: "20px",
        marginTop: "10px"
    };

    // Thank You Screen — shown immediately after successful submission
    if (showThankYou) {
        return (
            <div style={{
                backgroundImage: "url('/images/Login.png')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundAttachment: "fixed",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 0",
                fontFamily: "Inter, 'Segoe UI', Arial, sans-serif"
            }}>
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8} lg={6} xl={5}>
                            <Card style={{
                                backgroundColor: "rgba(255,255,255,0.95)",
                                padding: "2.5rem 2rem",
                                borderRadius: "20px",
                                boxShadow: "0 15px 40px rgba(0,0,0,0.15)",
                                border: "none",
                                textAlign: "center"
                            }}>
                                {/* Logo */}
                                <Link to="/"><img src="/images/LOGO.png" alt="TMGF" style={{ width: "160px", marginBottom: "20px" }} /></Link>

                                {/* Tick icon */}
                                <div style={{
                                    width: "80px", height: "80px", borderRadius: "50%",
                                    background: "linear-gradient(135deg, #2e7d32, #66bb6a)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    margin: "0 auto 20px", boxShadow: "0 6px 20px rgba(172,46,98,0.35)"
                                }}>
                                    <span style={{ fontSize: "36px", color: "white" }}>✓</span>
                                </div>

                                {/* Thank you message */}
                                <h2 style={{ color: "#2e7d32", fontWeight: "800", marginBottom: "8px" }}>
                                    {isFirstTime
                                        ? `Thank You for Registering${registeredName ? `, ${registeredName.split(" ")[0]}` : ""}!`
                                        : `Welcome Back${registeredName ? `, ${registeredName.split(" ")[0]}` : ""}!`}
                                </h2>
                                <p style={{ color: "#555", fontSize: "15px", marginBottom: "24px" }}>
                                    {isFirstTime
                                        ? "You have been successfully registered at TMGF. Welcome to our community!"
                                        : "Welcome back! Your visit has been successfully logged at TMGF."}
                                </p>
                                {/* Complete Profile prompt for secondary returning visitors */}
                                {isSecondaryReturning && !profileJustCompleted && (
                                    <div style={{
                                        background: "#fffbea",
                                        border: "1.5px solid #f0c040",
                                        borderRadius: "12px",
                                        padding: "16px",
                                        marginBottom: "16px",
                                        textAlign: "left"
                                    }}>
                                        <p style={{ fontWeight: "700", color: "#7c5200", marginBottom: "6px", fontSize: "14px" }}>
                                            📋 Your profile is incomplete!
                                        </p>
                                        <p style={{ fontSize: "13px", color: "#666", marginBottom: "12px" }}>
                                            Would you like to complete your profile? It helps TMGF stay in touch with you.
                                        </p>
                                        <div className="d-flex gap-2">
                                            <Button
                                                onClick={() => {
                                                    setShowThankYou(false);
                                                    setFoundVisitorId(registeredVisitorId);
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        fullName: registeredName || "",
                                                        mobileNumber: identifier || "",
                                                    }));
                                                    setUpdateStep(2);
                                                    setIsUpdating(true);
                                                }}
                                                style={{
                                                    background: "#ac2e62", border: "none",
                                                    borderRadius: "8px", padding: "10px 20px",
                                                    fontWeight: "700", fontSize: "13px",
                                                    color: "white", flex: 1
                                                }}
                                            >
                                                ✏️ Complete Profile
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    setShowThankYou(false);
                                                    setIsFirstTime(null);
                                                    setIsSecondaryReturning(false);
                                                    setRegisteredVisitorId("");
                                                    setRegisteredName("");
                                                    setIdentifier("");
                                                    setConsentChecked(false);
                                                }}
                                                style={{
                                                    background: "white", border: "1.5px solid #ccc",
                                                    borderRadius: "8px", padding: "10px 20px",
                                                    fontWeight: "700", fontSize: "13px",
                                                    color: "#666", flex: 1
                                                }}
                                            >
                                                Skip for Now
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                {/* Visitor ID Box */}
                                {registeredVisitorId && (
                                    <div style={{
                                        background: "linear-gradient(135deg, #fdf2f6, #fce8f0)",
                                        border: "2px solid #ac2e62",
                                        borderRadius: "14px",
                                        padding: "20px",
                                        marginBottom: "24px"
                                    }}>
                                        <p style={{ fontSize: "12px", fontWeight: "700", color: "#888", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "8px" }}>
                                            Your Visitor ID
                                        </p>
                                        <p style={{
                                            fontSize: "26px", fontWeight: "900", color: "#ac2e62",
                                            letterSpacing: "2px", marginBottom: "8px",
                                            fontFamily: "monospace"
                                        }}>
                                            {registeredVisitorId}
                                        </p>
                                        <p style={{ fontSize: "12px", color: "#888", marginBottom: "0" }}>
                                            Please show this ID to the NGO staff for verification
                                        </p>
                                    </div>
                                )}
                                {/* Back button */}
                                <Button
                                    onClick={() => {
                                        setShowThankYou(false);
                                        setIsFirstTime(null);
                                        setFormData({
                                            fullName: "", mobileNumber: "", email: "", address: "",
                                            gender: "", dob: "", ageGroup: "", area: "", city: "", state: "", pincode: "",
                                            occupation: "", organizationName: "", branch: "", purposeOfVisit: "",
                                            interestedCauses: [], preferredContactMethod: "WhatsApp", receiveUpdates: "Yes", notes: ""
                                        });
                                        setIdentifier("");
                                        setConsentChecked(false);
                                        setRegisteredVisitorId("");
                                        setRegisteredName("");
                                    }}
                                    style={{
                                        background: "#ac2e62", border: "none",
                                        borderRadius: "10px", padding: "12px 30px",
                                        fontWeight: "700", fontSize: "14px",
                                        color: "white", width: "100%",
                                        boxShadow: "0 4px 15px rgba(172,46,98,0.3)"
                                    }}
                                >
                                    ← Back to Home
                                </Button>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    // Step 1: Initial Selection Screen
    if (isFirstTime === null && !isUpdating) {
        return (
            <div style={{
                backgroundImage: "url('/images/Login.png')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundAttachment: "fixed",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                padding: "40px 0",
                fontFamily: "Inter, 'Segoe UI', Arial, sans-serif"
            }}>
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8} lg={6} xl={5}>
                            <Card style={{
                                backgroundColor: "rgba(255, 255, 255, 0.92)",
                                padding: "3rem 2.5rem",
                                borderRadius: "16px",
                                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
                                border: "1px solid rgba(0, 0, 0, 0.08)",
                                textAlign: "center"
                            }}>
                                {/* Centered Logo */}
                                <Link to="/" className="mb-4 d-inline-block">
                                    <img src="/images/LOGO.png" alt="TMGF Logo" style={{ width: "190px", height: "auto" }} />
                                </Link>

                                {/* Welcoming Headers */}
                                <h3 className="fw-bold mb-2" style={{ color: "#2c3e50" }}>Welcome to TMGF!</h3>
                                <p className="text-muted small mb-4 mx-auto" style={{ maxWidth: "380px" }}>
                                    Please select one of the options below to record your visit today.
                                </p>

                                {/* Vertically Stacked Options */}
                                <div className="mb-4 text-center" style={{
                                    backgroundColor: "rgba(172, 46, 98, 0.04)",
                                    border: "1px solid rgba(172, 46, 98, 0.15)",
                                    borderLeft: "4px solid #ac2e62",
                                    borderRadius: "12px",
                                    padding: "1.5rem",
                                    boxShadow: "0 4px 12px rgba(172, 46, 98, 0.03)"
                                }}>
                                    <h5 className="fw-bold mb-1" style={{ color: "#ac2e62", fontSize: "1.15rem" }}>First-Time Visitor</h5>
                                    <p className="text-muted small mb-3 mx-auto" style={{ maxWidth: "340px", lineHeight: "1.4" }}>
                                        Choose this option if you are visiting our NGO for the first time and need to register a profile.
                                    </p>
                                    <Button
                                        className="w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => {
                                            setIsFirstTime(true);
                                            setServerMsg("");
                                        }}
                                        onMouseEnter={() => setHoveredBtnNew(true)}
                                        onMouseLeave={() => setHoveredBtnNew(false)}
                                        style={{
                                            background: hoveredBtnNew ? '#2bb1ce' : '#ac2e62',
                                            color: '#fff',
                                            borderRadius: 999,
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            border: hoveredBtnNew ? '2px solid #2bb1ce' : '2px solid #ac2e62',
                                            boxShadow: 'none',
                                            letterSpacing: '0.5px',
                                            fontSize: '0.9rem',
                                            padding: '0.6rem 1.5rem',
                                            height: 'auto',
                                            transition: 'background 0.2s ease, color 0.2s ease, border-color 0.2s ease'
                                        }}
                                    >
                                        <span style={{
                                            background: '#fff',
                                            color: hoveredBtnNew ? '#2bb1ce' : '#ac2e62',
                                            borderRadius: '50%',
                                            width: 22,
                                            height: 22,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 16,
                                            marginRight: 8,
                                            fontWeight: 900,
                                            transition: 'background 0.2s ease, color 0.2s ease',
                                            lineHeight: 1
                                        }}>+</span>
                                        Register & Check In
                                    </Button>
                                </div>

                                <div className="text-center" style={{
                                    backgroundColor: "rgba(44, 62, 80, 0.04)",
                                    border: "1px solid rgba(44, 62, 80, 0.15)",
                                    borderLeft: "4px solid #2c3e50",
                                    borderRadius: "12px",
                                    padding: "1.5rem",
                                    boxShadow: "0 4px 12px rgba(44, 62, 80, 0.03)"
                                }}>
                                    <h5 className="fw-bold mb-1" style={{ color: "#2c3e50", fontSize: "1.15rem" }}>Been Here Before</h5>
                                    <p className="text-muted small mb-3 mx-auto" style={{ maxWidth: "340px", lineHeight: "1.4" }}>
                                        Choose this if you already have a registered profile and want to quickly log a new visit.
                                    </p>
                                    <Button
                                        className="w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => {
                                            setIsFirstTime(false);
                                            setServerMsg("");
                                        }}
                                        onMouseEnter={() => setHoveredBtnRepeat(true)}
                                        onMouseLeave={() => setHoveredBtnRepeat(false)}
                                        style={{
                                            background: hoveredBtnRepeat ? '#2bb1ce' : '#2c3e50',
                                            color: '#fff',
                                            borderRadius: 999,
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            border: hoveredBtnRepeat ? '2px solid #2bb1ce' : '2px solid #2c3e50',
                                            boxShadow: 'none',
                                            letterSpacing: '0.5px',
                                            fontSize: '0.9rem',
                                            padding: '0.6rem 1.5rem',
                                            height: 'auto',
                                            transition: 'background 0.2s ease, color 0.2s ease, border-color 0.2s ease'
                                        }}
                                    >
                                        <span style={{
                                            background: '#fff',
                                            color: hoveredBtnRepeat ? '#2bb1ce' : '#2c3e50',
                                            borderRadius: '50%',
                                            width: 22,
                                            height: 22,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 14,
                                            marginRight: 8,
                                            fontWeight: 900,
                                            transition: 'background 0.2s ease, color 0.2s ease',
                                            lineHeight: 1
                                        }}>↻</span>
                                        Quick Check In
                                    </Button>
                                </div>
                                <div className="mt-3">
                                    <button
                                        onClick={() => { setIsUpdating(true); setServerMsg(""); }}
                                        style={{
                                            background: 'rgba(172, 46, 98, 0.05)',
                                            border: '1px solid rgba(172, 46, 98, 0.2)',
                                            color: '#ac2e62',
                                            fontWeight: '700',
                                            fontSize: '0.85rem',
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            padding: '6px 16px',
                                            borderRadius: '20px',
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = 'rgba(172, 46, 98, 0.1)';
                                            e.target.style.borderColor = 'rgba(172, 46, 98, 0.4)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'rgba(172, 46, 98, 0.05)';
                                            e.target.style.borderColor = 'rgba(172, 46, 98, 0.2)';
                                        }}
                                    >
                                        ✏️ Update My Details
                                    </button>
                                </div>
                                <Link to="/" className="text-decoration-none text-muted small fw-bold mt-4 d-inline-block">
                                    ← Back to Homepage
                                </Link>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    // Update Details Screen
    if (isUpdating) {
        return (
            <div style={{
                backgroundImage: "url('/images/Login.png')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundAttachment: "fixed",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                padding: "40px 0",
                fontFamily: "Inter, 'Segoe UI', Arial, sans-serif"
            }}>
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8} lg={6} xl={5}>
                            <Card style={{
                                backgroundColor: "rgba(255, 255, 255, 0.92)",
                                padding: "2rem",
                                borderRadius: "15px",
                                boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                                border: "none"
                            }}>
                                <div className="text-center mb-4">
                                    <Link to="/"><img src="/images/LOGO.png" alt="TMGF Logo" style={{ width: "180px", marginBottom: "15px" }} /></Link>
                                    <h2 style={{ color: "#2c3e50", fontWeight: "800", letterSpacing: "-0.5px" }}>Update My Details</h2>
                                    <p className="text-muted">Find your profile and update your information.</p>
                                </div>

                                <Button variant="outline-secondary" size="sm" className="fw-bold mb-4"
                                    style={{ borderRadius: "6px" }}
                                    onClick={() => { setIsUpdating(false); setUpdateStep(1); setUpdateIdentifier(""); setFoundVisitorId(""); }}>
                                    ← Back
                                </Button>

                                {/* Step 1 — Enter identifier */}
                                {updateStep === 1 && (
                                    <div>
                                        <div style={sectionTitleStyle}>🔍 Find Your Profile</div>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-bold small text-uppercase text-muted">Registered Email or Mobile Number</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter your registered email or 10-digit mobile"
                                                value={updateIdentifier}
                                                onChange={(e) => setUpdateIdentifier(e.target.value)}
                                                style={inputStyle}
                                            />
                                        </Form.Group>
                                        <Button
                                            className="w-100 py-3 fw-bold text-uppercase"
                                            onClick={handleLookupForUpdate}
                                            disabled={isSubmitting || !updateIdentifier.trim()}
                                            style={{
                                                background: (isSubmitting || !updateIdentifier.trim()) ? "#bdc3c7" : "#ac2e62",
                                                border: "none",
                                                borderRadius: "10px",
                                                letterSpacing: "1px",
                                                boxShadow: (isSubmitting || !updateIdentifier.trim()) ? "none" : "0 4px 15px rgba(172, 46, 98, 0.3)",
                                                cursor: (isSubmitting || !updateIdentifier.trim()) ? "not-allowed" : "pointer",
                                                transition: "background 0.3s ease"
                                            }}
                                        >
                                            {isSubmitting ? <Spinner animation="border" size="sm" /> : "Find My Profile"}
                                        </Button>
                                    </div>
                                )}

                                {/* Step 2 — Update form (reuse same fields) */}
                                {updateStep === 2 && (
                                    <Form onSubmit={handleUpdateSubmit}>
                                        <div style={sectionTitleStyle}>📍 Basic Information</div>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold small text-uppercase text-muted">Full Name *</Form.Label>
                                                    <Form.Control type="text" name="fullName" value={formData.fullName} onChange={handleChange} isInvalid={!!errors.fullName} style={inputStyle} />
                                                    <Form.Control.Feedback type="invalid">{errors.fullName}</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold small text-uppercase text-muted">Mobile Number *</Form.Label>
                                                    <Form.Control type="tel" name="mobileNumber" value={formData.mobileNumber} onChange={handleChange} isInvalid={!!errors.mobileNumber} maxLength={10} style={inputStyle} />
                                                    <Form.Control.Feedback type="invalid">{errors.mobileNumber}</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold small text-uppercase text-muted">Email Address </Form.Label>
                                            <Form.Control 
                                                type="email" 
                                                name="email" 
                                                value={formData.email} 
                                                onChange={handleChange} 
                                                onBlur={(e) => {
                                                    const emailRegex = /^[a-zA-Z0-9._%+-]+@(gmail\.com|yahoo\.com|outlook\.com|hotmail\.com|icloud\.com|rediffmail\.com|ymail\.com|protonmail\.com|live\.com|msn\.com|me\.com|coep\.ac\.in|tmgf\.in|[a-zA-Z0-9-]+\.(edu|ac\.in|org|gov|nic\.in))$/i;
                                                    const val = e.target.value.trim();
                                                    if (val && !emailRegex.test(val)) {
                                                        setErrors(prev => ({ ...prev, email: "Please enter a valid email address (e.g. name@gmail.com)" }));
                                                    } else {
                                                        setErrors(prev => ({ ...prev, email: "" }));
                                                    }
                                                }}
                                                isInvalid={!!errors.email} 
                                                style={inputStyle} 
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-bold small text-uppercase text-muted">Full Address </Form.Label>
                                            <Form.Control as="textarea" rows={2} name="address" value={formData.address} onChange={handleChange} isInvalid={!!errors.address} style={inputStyle} />
                                            <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
                                        </Form.Group>

                                        <div style={sectionTitleStyle}>👤 Personal & Location Details</div>
                                        <Row>
                                             <Col md={4} xs={12}>
                                                 <Form.Group className="mb-3">
                                                     <Form.Label className="fw-bold small text-uppercase text-muted">Pincode</Form.Label>
                                                     <Form.Control type="text" name="pincode" value={formData.pincode} onChange={handleChange} isInvalid={!!errors.pincode} maxLength={6} style={inputStyle} />
                                                     <Form.Control.Feedback type="invalid">{errors.pincode}</Form.Control.Feedback>
                                                 </Form.Group>
                                             </Col>
                                             <Col md={4} xs={6}><Form.Group className="mb-3"><Form.Label className="fw-bold small text-uppercase text-muted">City</Form.Label><Form.Control type="text" name="city" value={formData.city} onChange={handleChange} style={inputStyle} /></Form.Group></Col>
                                             <Col md={4} xs={6}><Form.Group className="mb-3"><Form.Label className="fw-bold small text-uppercase text-muted">State</Form.Label><Form.Control type="text" name="state" value={formData.state} onChange={handleChange} style={inputStyle} /></Form.Group></Col>
                                        </Row>
                                        <Row>
                                             <Col md={4} xs={12}>
                                                 <Form.Group className="mb-3">
                                                     <Form.Label className="fw-bold small text-uppercase text-muted">Area / Locality</Form.Label>
                                                     <Form.Control type="text" name="area" placeholder="e.g. Model Town" value={formData.area} onChange={handleChange} style={inputStyle} />
                                                 </Form.Group>
                                             </Col>
                                             <Col md={4} xs={6}>
                                                 <Form.Group className="mb-3">
                                                     <Form.Label className="fw-bold small text-uppercase text-muted">Gender</Form.Label>
                                                     <Form.Select name="gender" value={formData.gender} onChange={handleChange} style={inputStyle}>
                                                         <option value="">Select</option>
                                                         <option value="Male">Male</option>
                                                         <option value="Female">Female</option>
                                                         <option value="Other">Other</option>
                                                     </Form.Select>
                                                 </Form.Group>
                                             </Col>
                                             <Col md={4} xs={6}>
                                                 <Form.Group className="mb-3">
                                                     <Form.Label className="fw-bold small text-uppercase text-muted">Date of Birth</Form.Label>
                                                     <Form.Control
                                                         type="date"
                                                         name="dob"
                                                         value={formData.dob || ""}
                                                         onChange={handleChange}
                                                         isInvalid={!!errors.dob}
                                                         style={inputStyle}
                                                         max={new Date().toISOString().split("T")[0]}
                                                     />
                                                     <Form.Control.Feedback type="invalid">{errors.dob}</Form.Control.Feedback>
                                                 </Form.Group>
                                                 {formData.dob && (
                                                     <div style={{ marginTop: "-8px", marginBottom: "15px", fontSize: "0.85rem", color: "#ac2e62", fontWeight: "600" }}>
                                                         Age: {calculateAge(formData.dob)} years</div>
                                                 )}
                                             </Col>
                                        </Row>

                                        <div style={sectionTitleStyle}>💼 Professional Details</div>
                                        <Row>
                                            <Col md={6}><Form.Group className="mb-3"><Form.Label className="fw-bold small text-uppercase text-muted">Occupation</Form.Label><Form.Control type="text" name="occupation" value={formData.occupation} onChange={handleChange} style={inputStyle} /></Form.Group></Col>
                                            <Col md={6}><Form.Group className="mb-3"><Form.Label className="fw-bold small text-uppercase text-muted">Organization</Form.Label><Form.Control type="text" name="organizationName" value={formData.organizationName} onChange={handleChange} style={inputStyle} /></Form.Group></Col>
                                        </Row>

                                        <div style={sectionTitleStyle}>📧 Preferences</div>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold small text-uppercase text-muted">Preferred Contact Method</Form.Label>
                                                    <Form.Select name="preferredContactMethod" value={formData.preferredContactMethod} onChange={handleChange} style={inputStyle}>
                                                        <option value="Phone">Phone Call</option>
                                                        <option value="Email">Email</option>
                                                        <option value="WhatsApp">WhatsApp</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-bold small text-uppercase text-muted">Receive NGO Updates?</Form.Label>
                                                    <Form.Select name="receiveUpdates" value={formData.receiveUpdates} onChange={handleChange} style={inputStyle}>
                                                        <option value="Yes">Yes, keep me updated</option>
                                                        <option value="No">No, thanks</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Button
                                            className="w-100 py-3 fw-bold text-uppercase mt-4"
                                            type="submit"
                                            disabled={isSubmitting}
                                            style={{
                                                background: isSubmitting ? "#bdc3c7" : "#ac2e62",
                                                border: "none",
                                                borderRadius: "10px",
                                                letterSpacing: "1px",
                                                boxShadow: isSubmitting ? "none" : "0 4px 15px rgba(172, 46, 98, 0.3)",
                                                cursor: isSubmitting ? "not-allowed" : "pointer",
                                                transition: "background 0.3s ease"
                                            }}
                                        >
                                            {isSubmitting ? <Spinner animation="border" size="sm" /> : "Save Changes"}
                                        </Button>
                                    </Form>
                                )}
                            </Card>
                        </Col>
                    </Row>
                </Container>
                <ToastContainer />
            </div>
        );
    }

    // Step 2: Form Display (either returning visitor minimal form, or new visitor full form)
    return (
        <div style={{
            backgroundImage: "url('/images/Login.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            padding: "40px 0"
        }}>
            <Container>
                <Row className="justify-content-center">
                    <Col lg={isFirstTime ? 9 : 6} xl={isFirstTime ? 8 : 5}>
                        <Card style={{
                            backgroundColor: "rgba(255, 255, 255, 0.92)",
                            padding: "2rem",
                            borderRadius: "15px",
                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
                            border: "none"
                        }}>
                            <div className="text-center mb-4">
                                <Link to="/">
                                    <img src="/images/LOGO.png" alt="TMGF Logo" style={{ width: "180px", marginBottom: "15px" }} />
                                </Link>
                                <h2 style={{ color: "#2c3e50", fontWeight: "800", letterSpacing: "-0.5px" }}>
                                    {isFirstTime ? "Visitor Registration" : "Returning Check-in"}
                                </h2>
                                <p className="text-muted">
                                    {isFirstTime
                                        ? "Register your profile and log your visit to stay connected with us."
                                        : "Quickly record a new visit under your registered profile."}
                                </p>
                            </div>

                            {/* Back to Step 1 Button */}
                            <div className="mb-4">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => {
                                        setIsFirstTime(null);
                                        setServerMsg("");
                                        setErrors({});
                                        setConsentChecked(false);
                                    }}
                                    className="fw-bold"
                                    style={{ borderRadius: "6px" }}
                                >
                                    ← Change Option
                                </Button>
                            </div>

                            {serverMsg && (
                                <Alert variant={serverMsg.includes("Success") ? "success" : "warning"} className="mb-4 text-center fw-bold">
                                    {serverMsg}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                {/* FIRST TIME VISITOR FLOW: FULL FORM */}
                                {isFirstTime ? (
                                    <>
                                        {/* Basic Information */}
                                        <div style={sectionTitleStyle}>📍 Basic Information</div>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold small text-uppercase text-muted">Full Name *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="fullName"
                                                        placeholder="John Doe"
                                                        value={formData.fullName}
                                                        onChange={handleChange}
                                                        isInvalid={!!errors.fullName}
                                                        style={inputStyle}
                                                    />
                                                    <Form.Control.Feedback type="invalid">{errors.fullName}</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold small text-uppercase text-muted">Mobile Number *</Form.Label>
                                                    <Form.Control
                                                        type="tel"
                                                        name="mobileNumber"
                                                        placeholder="9876543210"
                                                        value={formData.mobileNumber}
                                                        onChange={handleChange}
                                                        isInvalid={!!errors.mobileNumber}
                                                        style={inputStyle}
                                                        maxLength={10}
                                                    />
                                                    <Form.Control.Feedback type="invalid">{errors.mobileNumber}</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md={12}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold small text-uppercase text-muted">Email Address </Form.Label>
                                                    <Form.Control
                                                        type="email"
                                                        name="email"
                                                        placeholder="john@example.com"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        isInvalid={!!errors.email}
                                                        style={inputStyle}
                                                    />
                                                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold small text-uppercase text-muted">Branch *</Form.Label>
                                            <Form.Select
                                                name="branch"
                                                value={formData.branch}
                                                onChange={handleChange}
                                                isInvalid={!!errors.branch}
                                                style={inputStyle}
                                            >
                                                <option value="">Select Branch</option>
                                                <option value="Maainagari">Maainagari</option>
                                                <option value="Shree Manashanti Chhatralaya">Shree Manashanti Chhatralaya</option>
                                                <option value="TMGF Central Office">TMGF Central Office</option>
                                                <option value="Maainagari - Solapur">Maainagari - Solapur</option>
                                                <option value="Maainagari - Goa">Maainagari - Goa</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">{errors.branch}</Form.Control.Feedback>
                                        </Form.Group>

                                        {/* Personal & Location */}
                                        <div style={sectionTitleStyle}>👤 Personal & Location Details</div>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold small text-uppercase text-muted">Full Address </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="address"
                                                placeholder="Enter your current address"
                                                value={formData.address}
                                                onChange={handleChange}
                                                isInvalid={!!errors.address}
                                                style={inputStyle}
                                            />
                                        </Form.Group>
                                        <Row>
                                             <Col md={4} xs={12}>
                                                 <Form.Group className='mb-3'>
                                                     <Form.Label className='fw-bold small text-uppercase text-muted'>Pincode</Form.Label>
                                                     <Form.Control
                                                         type='text'
                                                         name='pincode'
                                                         placeholder='411001'
                                                         value={formData.pincode}
                                                         onChange={handleChange}
                                                         isInvalid={!!errors.pincode}
                                                         style={inputStyle}
                                                         maxLength={6}
                                                     />
                                                     <Form.Control.Feedback type='invalid'>{errors.pincode}</Form.Control.Feedback>
                                                 </Form.Group>
                                             </Col>
                                             <Col md={4} xs={6}>
                                                 <Form.Group className='mb-3'>
                                                     <Form.Label className='fw-bold small text-uppercase text-muted'>City</Form.Label>
                                                     <Form.Control type='text' name='city' placeholder='City' value={formData.city} onChange={handleChange} style={inputStyle} />
                                                 </Form.Group>
                                             </Col>
                                             <Col md={4} xs={6}>
                                                 <Form.Group className='mb-3'>
                                                     <Form.Label className='fw-bold small text-uppercase text-muted'>State</Form.Label>
                                                     <Form.Control type='text' name='state' placeholder='State' value={formData.state} onChange={handleChange} style={inputStyle} />
                                                 </Form.Group>
                                             </Col>
                                        </Row>
                                        <Row>
                                             <Col md={4} xs={12}>
                                                 <Form.Group className='mb-3'>
                                                     <Form.Label className='fw-bold small text-uppercase text-muted'>Area / Locality</Form.Label>
                                                     <Form.Control type='text' name='area' placeholder='e.g. Model Town' value={formData.area} onChange={handleChange} style={inputStyle} />
                                                 </Form.Group>
                                             </Col>
                                             <Col md={4} xs={6}>
                                                 <Form.Group className='mb-3'>
                                                     <Form.Label className='fw-bold small text-uppercase text-muted'>Gender</Form.Label>
                                                     <Form.Select name='gender' value={formData.gender} onChange={handleChange} style={inputStyle}>
                                                         <option value=''>Select</option>
                                                         <option value='Male'>Male</option>
                                                         <option value='Female'>Female</option>
                                                         <option value='Other'>Other</option>
                                                     </Form.Select>
                                                 </Form.Group>
                                             </Col>
                                             <Col md={4} xs={6}>
                                                 <Form.Group className='mb-3'>
                                                     <Form.Label className='fw-bold small text-uppercase text-muted'>Date of Birth</Form.Label>
                                                     <Form.Control
                                                         type='date'
                                                         name='dob'
                                                         value={formData.dob || ''}
                                                         onChange={handleChange}
                                                         isInvalid={!!errors.dob}
                                                         style={inputStyle}
                                                         max={new Date().toISOString().split('T')[0]}
                                                     />
                                                     <Form.Control.Feedback type='invalid'>{errors.dob}</Form.Control.Feedback>
                                                 </Form.Group>
                                                 {formData.dob && (
                                                     <div style={{ marginTop: '-8px', marginBottom: '15px', fontSize: '0.85rem', color: '#ac2e62', fontWeight: '600' }}>
                                                         Age: {calculateAge(formData.dob)} years</div>
                                                 )}
                                             </Col>
                                        </Row>

                                        {/* Professional Info */}
                                        <div style={sectionTitleStyle}>💼 Professional Details</div>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold small text-uppercase text-muted">Occupation</Form.Label>
                                                    <Form.Control type="text" name="occupation" placeholder="e.g. Software Engineer" value={formData.occupation} onChange={handleChange} style={inputStyle} />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold small text-uppercase text-muted">Organization / Company</Form.Label>
                                                    <Form.Control type="text" name="organizationName" placeholder="e.g. Google" value={formData.organizationName} onChange={handleChange} style={inputStyle} />
                                                </Form.Group>
                                            </Col>
                                        </Row>


                                        {/* Visit Purpose */}
                                        <div style={sectionTitleStyle}>📝 Visit Purpose & Interests</div>
                                        <Row>
                                            <Col md={12}>
                                                <Form.Group className="mb-4">
                                                    <Form.Label className="fw-bold small text-uppercase text-muted">Primary Purpose of Visit *</Form.Label>
                                                    <Form.Select name="purposeOfVisit" value={formData.purposeOfVisit} onChange={handleChange} style={inputStyle} isInvalid={!!errors.purposeOfVisit}>
                                                    <option value="">-- Select Purpose --</option>
                                                    <option value="General Inquiry">General Inquiry</option>
                                                    <option value="Volunteer Interest">Volunteer Interest</option>
                                                    <option value="Donation Related">Donation Related</option>
                                                    <option value="Event Participation">Event Participation</option>
                                                    <option value="Partnership Discussion">Partnership Discussion</option>
                                                    <option value="NGO Visit">NGO Visit</option>
                                                    <option value="Other">Other</option>
                                                </Form.Select>
                                                {formData.purposeOfVisit === "Other" && (
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Please describe your purpose of visit..."
                                                        value={otherPurpose}
                                                        onChange={(e) => {
                                                            setOtherPurpose(e.target.value);
                                                            if (e.target.value.trim()) setErrors(prev => ({ ...prev, purposeOfVisit: "" }));
                                                        }}
                                                        style={{ ...inputStyle, marginTop: '10px' }}
                                                        isInvalid={!!errors.purposeOfVisit && !otherPurpose.trim()}
                                                    />
                                                )}
                                                <Form.Control.Feedback type="invalid">{errors.purposeOfVisit}</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-bold small text-uppercase text-muted d-block mb-3">Interested Causes (Pick all that apply)</Form.Label>
                                            <div className="d-flex flex-wrap gap-2">
                                                {causesOptions.map(cause => (
                                                    <Badge
                                                        key={cause}
                                                        pill
                                                        bg={formData.interestedCauses.includes(cause) ? "primary" : "light"}
                                                        className={`py-2 px-3 border cursor-pointer ${formData.interestedCauses.includes(cause) ? "" : "text-muted"}`}
                                                        style={{
                                                            cursor: "pointer",
                                                            fontSize: "0.85rem",
                                                            backgroundColor: formData.interestedCauses.includes(cause) ? "#ac2e62" : "#f8f9fa",
                                                            borderColor: formData.interestedCauses.includes(cause) ? "#ac2e62" : "#dee2e6"
                                                        }}
                                                        onClick={() => handleInterestChange(cause)}
                                                    >
                                                        {cause}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </Form.Group>

                                        {/* Communication Preferences */}
                                        <div style={sectionTitleStyle}>📧 Preferences & Notes</div>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold small text-uppercase text-muted">Preferred Contact Method</Form.Label>
                                                    <Form.Select name="preferredContactMethod" value={formData.preferredContactMethod} onChange={handleChange} style={inputStyle}>
                                                        <option value="Phone">Phone Call</option>
                                                        <option value="Email">Email</option>
                                                        <option value="WhatsApp">WhatsApp</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-bold small text-uppercase text-muted">Receive NGO Updates?</Form.Label>
                                                    <Form.Select name="receiveUpdates" value={formData.receiveUpdates} onChange={handleChange} style={inputStyle}>
                                                        <option value="Yes">Yes, keep me updated</option>
                                                        <option value="No">No, thanks</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>


                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-bold small text-uppercase text-muted">Additional Information / Remarks</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="notes"
                                                placeholder="Any specific questions or messages for our team?"
                                                value={formData.notes}
                                                onChange={handleChange}
                                                style={inputStyle}
                                            />
                                        </Form.Group>
                                    </>
                                ) : (
                                    <>
                                        {/* RETURNING VISITOR FLOW: MINIMAL FORM */}
                                        <div style={sectionTitleStyle}>🔁 Visitor Check-in Details</div>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold small text-uppercase text-muted">Registered Email or Mobile Number *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter your registered email or 10-digit mobile number"
                                                value={identifier}
                                                onChange={(e) => {
                                                    setIdentifier(e.target.value);
                                                    if (errors.identifier) setErrors(prev => ({ ...prev, identifier: "" }));
                                                }}
                                                isInvalid={!!errors.identifier}
                                                style={inputStyle}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.identifier}</Form.Control.Feedback>
                                            {isLookingUp && (
                                                <div className="small text-muted mt-1" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <Spinner animation="border" size="sm" variant="primary" style={{ width: '14px', height: '14px' }} />
                                                    Checking profile...
                                                </div>
                                            )}
                                            {!isLookingUp && resolvedName && (
                                                <div className={`small mt-1 fw-bold ${resolvedName === "Not Registered" ? "text-danger" : "text-success"}`}>
                                                    {resolvedName === "Not Registered" ? "❌ Profile not found" : `✅ Welcome back, ${resolvedName}!`}
                                                </div>
                                            )}
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold small text-uppercase text-muted">Primary Purpose of Visit *</Form.Label>
                                            <Form.Select name="purposeOfVisit" value={formData.purposeOfVisit} onChange={handleChange} style={inputStyle} isInvalid={!!errors.purposeOfVisit}>
                                                <option value="">-- Select Purpose --</option>
                                                <option value="General Inquiry">General Inquiry</option>
                                                <option value="Volunteer Interest">Volunteer Interest</option>
                                                <option value="Donation Related">Donation Related</option>
                                                <option value="Event Participation">Event Participation</option>
                                                <option value="Partnership Discussion">Partnership Discussion</option>
                                                <option value="NGO Visit">NGO Visit</option>
                                                <option value="Other">Other</option>
                                            </Form.Select>
                                            {formData.purposeOfVisit === "Other" && (
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Please describe your purpose of visit..."
                                                    value={otherPurpose}
                                                    onChange={(e) => {
                                                        setOtherPurpose(e.target.value);
                                                        if (e.target.value.trim()) setErrors(prev => ({ ...prev, purposeOfVisit: "" }));
                                                    }}
                                                    style={{ ...inputStyle, marginTop: '10px' }}
                                                    isInvalid={!!errors.purposeOfVisit && !otherPurpose.trim()}
                                                />
                                            )}
                                            <Form.Control.Feedback type="invalid">{errors.purposeOfVisit}</Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bold small text-uppercase text-muted">Branch *</Form.Label>
                                            <Form.Select
                                                name="branch"
                                                value={formData.branch}
                                                onChange={handleChange}
                                                isInvalid={!!errors.branch}
                                                style={inputStyle}
                                            >
                                                <option value="">Select Branch</option>
                                                <option value="Maainagari">Maainagari</option>
                                                <option value="Shree Manashanti Chhatralaya">Shree Manashanti Chhatralaya</option>
                                                <option value="TMGF Central Office">TMGF Central Office</option>
                                                <option value="Maainagari - Solapur">Maainagari - Solapur</option>
                                                <option value="Maainagari - Goa">Maainagari - Goa</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">{errors.branch}</Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-bold small text-uppercase text-muted">Additional Information / Remarks</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="notes"
                                                placeholder="Any specific comments for this visit?"
                                                value={formData.notes}
                                                onChange={handleChange}
                                                style={inputStyle}
                                            />
                                        </Form.Group>
                                    </>
                                )}

                                {/* Group Visit Toggle Checkbox */}
                                <div style={{
                                    background: "#f8f9fa",
                                    border: "1.5px dashed #ac2e62",
                                    borderRadius: "12px",
                                    padding: "16px 20px",
                                    marginBottom: "20px"
                                }}>
                                    <Form.Group className="d-flex align-items-center m-0" controlId="groupVisitCheckbox">
                                        <Form.Check
                                            type="checkbox"
                                            id="group-visit-check"
                                            checked={isGroupVisit}
                                            onChange={(e) => setIsGroupVisit(e.target.checked)}
                                            style={{
                                                cursor: "pointer",
                                                transform: "scale(1.2)",
                                                accentColor: "#ac2e62"
                                            }}
                                        />
                                        <Form.Label
                                            htmlFor="group-visit-check"
                                            className="mb-0 ms-3"
                                            style={{
                                                cursor: "pointer",
                                                userSelect: "none",
                                                fontSize: "0.95rem",
                                                fontWeight: "600",
                                                color: "#ac2e62"
                                            }}
                                        >
                                            I am checking in with a group
                                        </Form.Label>
                                    </Form.Group>
                                </div>

                                <div
                                    className="mb-4 mt-4 p-3 rounded"
                                    style={{
                                        background: consentChecked ? 'rgba(43, 177, 130, 0.08)' : 'rgba(172, 46, 98, 0.06)',
                                        border: consentChecked ? '1.5px solid rgba(43, 177, 130, 0.5)' : '1.5px solid rgba(172, 46, 98, 0.4)',
                                        transition: 'all 0.3s ease',
                                        borderRadius: '10px'
                                    }}
                                >
                                    <Form.Group className="d-flex align-items-start m-0" controlId="consentCheckbox">
                                        <Form.Check
                                            type="checkbox"
                                            id="consent-check"
                                            checked={consentChecked}
                                            onChange={(e) => setConsentChecked(e.target.checked)}
                                            style={{
                                                cursor: "pointer",
                                                transform: "scale(1.4)",
                                                accentColor: "#0d6efd"
                                            }}
                                        />
                                        <Form.Label
                                            htmlFor="consent-check"
                                            className="mb-0 ms-3"
                                            style={{
                                                cursor: "pointer",
                                                userSelect: "none",
                                                lineHeight: "1.5",
                                                fontSize: "0.9rem",
                                                fontWeight: "500",
                                                color: consentChecked ? '#1a7a55' : '#2c3e50'
                                            }}
                                        >
                                            I hereby consent to share my contact information with the organization, and I agree to be contacted for future updates, community programs, and volunteering opportunities.
                                            {!consentChecked && (
                                                <span className="d-block mt-1" style={{ color: '#ac2e62', fontSize: '0.8rem', fontWeight: '600' }}>
                                                    ⚠️ You must check this box to submit.
                                                </span>
                                            )}
                                        </Form.Label>
                                    </Form.Group>
                                </div>

                                <div className="d-flex gap-3 mt-4">
                                    <Button
                                        variant="outline-secondary"
                                        type="button"
                                        className="w-100 py-3 fw-bold text-uppercase"
                                        onClick={() => {
                                            setFormData({
                                                fullName: "", mobileNumber: "", email: "", address: "",
                                                gender: "", dob: "", ageGroup: "", area: "", city: "", state: "", pincode: "",
                                                occupation: "", organizationName: "", purposeOfVisit: "", branch: "",
                                                interestedCauses: [], preferredContactMethod: "WhatsApp", receiveUpdates: "Yes", notes: ""
                                            });
                                            setIdentifier("");
                                            setIsGroupVisit(false);
                                            setConsentChecked(false);
                                        }}
                                        style={{ borderRadius: "10px", letterSpacing: "1px" }}
                                    >
                                        Clear Form
                                    </Button>
                                    <Button
                                        className="w-100 py-3 fw-bold text-uppercase"
                                        type="submit"
                                        disabled={isSubmitting || !consentChecked}
                                        style={{
                                            background: (isSubmitting || !consentChecked) ? "#bdc3c7" : "#ac2e62",
                                            border: "none",
                                            borderRadius: "10px",
                                            letterSpacing: "1px",
                                            boxShadow: (isSubmitting || !consentChecked) ? "none" : "0 4px 15px rgba(172, 46, 98, 0.3)",
                                            cursor: (isSubmitting || !consentChecked) ? "not-allowed" : "pointer",
                                            transition: "background 0.3s ease"
                                        }}
                                    >
                                        {isSubmitting ? <Spinner animation="border" size="sm" /> : (isGroupVisit ? "Continue to Group Details" : "Submit Visit Log")}
                                    </Button>
                                </div>
                            </Form>

                            <div className="text-center mt-4">
                                <Link to="/" className="text-decoration-none text-muted small fw-bold">
                                    ← Back to Homepage
                                </Link>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <ToastContainer />
        </div>
    );
};

export default VisitorRegistration;


